import { Settings, ChevronRight, Sparkles } from "lucide-react";
import MindrailLogo from "./MindrailLogo";

/**
 * "Clean Mode" — 8-second seamless loop.
 *
 *   0.0–1.5s   BUSY        cluttered article page (banner / sidebar / cookies)
 *   1.5–2.5s   ACTIVATING  toggle slides to On, Balanced highlights, green pulse
 *   2.5–4.5s   FADING      distractions stagger-fade out
 *   4.5–6.0s   CALM        clean article + "Clear, focused, intact" success label
 *   6.0–8.0s   RESET       distractions stagger-fade back to busy state
 *
 * Pure DOM + CSS keyframes scoped under .mr-clean-mode.
 */
export default function CleanModeAnimation() {
  return (
    <div className="absolute inset-0 mr-clean-mode overflow-hidden">
      {/* Ambient sparkles around the card */}
      <span className="mr-cm-spark mr-cm-spark-1 absolute" style={{ left: "82%", top: "6%" }} aria-hidden>
        <Sparkles className="w-3 h-3 text-[#22C55E]" strokeWidth={2.2} />
      </span>
      <span className="mr-cm-spark mr-cm-spark-2 absolute" style={{ left: "6%", top: "44%" }} aria-hidden>
        <Sparkles className="w-2.5 h-2.5 text-[#22C55E]" strokeWidth={2.2} />
      </span>

      {/* ===== BROWSER WINDOW ===== */}
      <div className="absolute inset-x-3 top-3 bottom-[34%] bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_24px_60px_rgba(15,23,42,0.10)] overflow-hidden flex flex-col">
        {/* chrome */}
        <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-2.5 py-1.5 flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F9735B]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
          <div className="ml-2 flex-1 bg-white border border-[#E2E8F0] rounded-md px-2 py-0.5 text-[8px] text-[#475569] font-label truncate">
            designlab.co/blog/price-freelance-design-services
          </div>
          <ChevronRight className="w-2.5 h-2.5 text-[#94A3B8]" strokeWidth={2} />
        </div>

        {/* TOP BANNER (clutter — fades) */}
        <div className="mr-cm-banner shrink-0 px-2.5 pt-2">
          <div className="bg-[#EDE9FE] border border-[#7C3AED]/15 rounded-lg px-2.5 py-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-[#7C3AED]/15 flex items-center justify-center text-[#7C3AED] font-bold text-[10px]">
              Ad
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-[9px] font-bold text-[#0F172A] truncate">
                Try Solo for invoicing, contracts &amp; taxes
              </p>
              <p className="text-[8px] text-[#475569] truncate">
                Everything freelancers need, in one place.
              </p>
            </div>
            <span className="text-[8px] font-medium bg-[#7C3AED] text-white px-2 py-0.5 rounded">
              Start trial
            </span>
          </div>
        </div>

        {/* Body: sidebar + article */}
        <div className="flex-1 flex gap-2 px-2.5 py-2 min-h-0 relative">
          {/* LEFT SIDEBAR (clutter — fades) */}
          <div className="mr-cm-sidebar w-[28%] shrink-0 space-y-2">
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-2">
              <p className="font-display text-[8px] font-bold text-[#0F172A] leading-tight">
                Join 12,000+ freelancers
              </p>
              <p className="text-[7px] text-[#475569] mt-0.5 leading-tight">
                Get weekly tips on pricing.
              </p>
              <div className="mt-1.5 h-3 rounded-sm bg-white border border-[#E2E8F0]" />
              <div className="mt-1 h-3.5 rounded-sm bg-[#7C3AED] flex items-center justify-center text-[7px] text-white font-semibold">
                Subscribe
              </div>
            </div>

            <div className="space-y-1.5">
              <p className="font-label text-[7px] tracking-[0.15em] font-bold text-[#475569]">
                TRENDING NOW
              </p>
              {[
                "5 Pricing Mistakes That Cost Designers",
                "How to Handle Scope Creep (Without Losing Profit)",
                "Hourly vs Project Pricing — What Works Best?",
              ].map((t, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 p-1 rounded bg-white border border-[#E2E8F0]"
                >
                  <span className="w-6 h-6 rounded shrink-0 bg-gradient-to-br from-[#FEF3C7] to-[#FFE4DE]" />
                  <p className="font-display text-[7px] leading-tight text-[#0F172A] line-clamp-2">
                    {t}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ARTICLE (intact throughout) */}
          <div className="mr-cm-article flex-1 min-w-0 overflow-hidden">
            <p className="font-label text-[8px] tracking-[0.18em] font-bold text-[#22C55E]">
              BUSINESS
            </p>
            <p className="font-display text-[14px] font-bold text-[#0F172A] mt-1 leading-tight">
              How to Price Your Freelance Design Services in 2026
            </p>
            <p className="text-[9px] text-[#475569] mt-1.5 leading-snug">
              A practical framework to help you price with confidence and
              attract better clients.
            </p>
            <p className="text-[8px] text-[#94A3B8] mt-1.5 font-label">
              By Jordan Avery · May 9, 2026 · 9 min read
            </p>
            <div className="mt-1.5 flex gap-1">
              {["Pricing", "Freelancing", "Business"].map((t) => (
                <span
                  key={t}
                  className="text-[7px] font-medium px-1.5 py-0.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-[#475569]"
                >
                  {t}
                </span>
              ))}
            </div>
            {/* hero image (decorative plant + cup) */}
            <div className="mt-2 h-14 rounded-md overflow-hidden bg-gradient-to-br from-[#F1F5F9] via-[#FEF3C7]/40 to-[#DCFCE7]/60 relative">
              <svg viewBox="0 0 200 60" className="absolute inset-0 w-full h-full" aria-hidden>
                {/* table line */}
                <line x1="0" y1="50" x2="200" y2="50" stroke="#E2E8F0" strokeWidth="1" />
                {/* plant pot */}
                <rect x="60" y="38" width="22" height="14" rx="2" fill="#475569" opacity="0.55" />
                {/* leaves */}
                <path
                  d="M71 38 Q 64 22 60 12 Q 70 18 71 30 Q 76 16 88 14 Q 82 26 75 32 Q 84 30 92 22 Q 88 32 78 36 Z"
                  fill="#22C55E"
                  opacity="0.7"
                />
                {/* mug */}
                <rect x="110" y="36" width="16" height="16" rx="2" fill="#475569" opacity="0.6" />
                <path d="M126 40 q6 0 6 5 t-6 5" fill="none" stroke="#475569" strokeWidth="1.6" opacity="0.6" />
                {/* notebook */}
                <rect x="90" y="48" width="22" height="4" rx="1" fill="#F59E0B" opacity="0.55" />
              </svg>
            </div>
            {/* body lines */}
            <div className="mt-2 space-y-1">
              {[100, 92, 96, 88, 70].map((w, i) => (
                <span
                  key={i}
                  className="block h-1 rounded-full bg-[#E2E8F0]"
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* COOKIE BANNER (clutter — slides/fades) */}
        <div className="mr-cm-cookies absolute left-2 right-2 bottom-2 bg-white border border-[#E2E8F0] rounded-md shadow-[0_8px_24px_rgba(15,23,42,0.08)] px-2 py-1.5 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-[#FEF3C7] flex items-center justify-center text-[10px]">
            🍪
          </span>
          <p className="flex-1 text-[8px] text-[#0F172A] leading-tight min-w-0 truncate">
            We use cookies to improve your experience.
          </p>
          <span className="text-[8px] font-medium px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[#0F172A] whitespace-nowrap">
            Manage
          </span>
          <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-[#0F172A] text-white whitespace-nowrap">
            Accept
          </span>
        </div>
      </div>

      {/* ===== CLEAN MODE CONTROL CARD ===== */}
      <div className="mr-cm-card absolute right-4 bottom-4 w-[60%] max-w-[230px]">
        {/* green pulse rings */}
        <span className="mr-cm-pulse mr-cm-pulse-1 absolute inset-0 rounded-2xl" aria-hidden />
        <span className="mr-cm-pulse mr-cm-pulse-2 absolute inset-0 rounded-2xl" aria-hidden />

        <div className="relative bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_16px_40px_rgba(15,23,42,0.12)] p-3">
          {/* header */}
          <div className="flex items-center justify-between">
            <MindrailLogo size={36} />
            <Settings className="w-3.5 h-3.5 text-[#94A3B8]" strokeWidth={1.75} />
          </div>

          {/* status row */}
          <div className="mt-2.5 bg-[#DCFCE7]/60 border border-[#22C55E]/20 rounded-xl px-2.5 py-2 flex items-center gap-2">
            <CalmIcon />
            <div className="flex-1 min-w-0">
              <p className="font-label text-[8px] tracking-[0.18em] font-bold text-[#16A34A]">
                CALM VIEW
              </p>
              <p className="font-display text-[12px] font-bold text-[#0F172A] leading-tight">
                Clean Mode
              </p>
            </div>
            {/* toggle */}
            <span className="mr-cm-toggle relative inline-block w-9 h-5 rounded-full border border-[#E2E8F0]">
              <span className="mr-cm-toggle-knob absolute top-0.5 w-4 h-4 rounded-full bg-white shadow" />
              <span className="mr-cm-toggle-label absolute inset-0 flex items-center justify-center text-[7px] font-bold tracking-wider">
                <span className="mr-cm-toggle-off">OFF</span>
                <span className="mr-cm-toggle-on">ON</span>
              </span>
            </span>
          </div>

          {/* mode tabs */}
          <div className="mt-2 grid grid-cols-3 gap-1 p-1 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
            <span className="mr-cm-mode mr-cm-mode-soft text-center text-[9px] font-display font-semibold py-1 rounded-md">
              Soft
            </span>
            <span className="mr-cm-mode mr-cm-mode-bal text-center text-[9px] font-display font-semibold py-1 rounded-md">
              Balanced
            </span>
            <span className="mr-cm-mode mr-cm-mode-deep text-center text-[9px] font-display font-semibold py-1 rounded-md">
              Deep
            </span>
          </div>
        </div>
      </div>

      {/* ===== SUCCESS LABEL ===== */}
      <div className="mr-cm-success absolute left-4 bottom-5 inline-flex items-center gap-1.5 bg-[#DCFCE7] border border-[#22C55E]/30 rounded-full px-2.5 py-1 shadow-[0_6px_18px_rgba(34,197,94,0.18)]">
        <Sparkles className="w-3 h-3 text-[#16A34A]" strokeWidth={2.2} />
        <span className="font-display text-[9px] font-bold text-[#16A34A] whitespace-nowrap">
          Clear, focused, intact
        </span>
      </div>
    </div>
  );
}

function CalmIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" aria-hidden>
      <path
        d="M12 3 Q 8 9 6 12 Q 4 16 7 19 Q 12 21 17 19 Q 20 16 18 12 Q 16 9 12 3 Z"
        fill="#22C55E"
        opacity="0.9"
      />
    </svg>
  );
}
